/**
 * Product Lens Zoom
 * A circular magnifier lens that follows the cursor over product images.
 * Shows a zoomed-in portion of the image inside the lens circle.
 */

const LENS_SIZE = 224;       // diameter of the lens circle in px
const ZOOM_FACTOR = 2.5;     // how much to zoom inside the lens

class ProductLensZoom {
  /** @type {HTMLElement | null} */
  #currentContainer = null;

  /** @type {HTMLDivElement | null} */
  #lens = null;

  /** @type {AbortController | null} */
  #controller = null;

  constructor() {
    // Wait for DOM then init
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#init());
    } else {
      this.#init();
    }
  }

  #init() {
    this.#createLens();
    this.#attachToGalleries();

    // Re-attach when Shopify section renders (theme editor)
    document.addEventListener('shopify:section:load', () => this.#attachToGalleries());
  }

  #createLens() {
    const lens = document.createElement('div');
    lens.id = 'product-lens';
    lens.setAttribute('aria-hidden', 'true');
    lens.style.cssText = `
      position: fixed;
      width: ${LENS_SIZE}px;
      height: ${LENS_SIZE}px;
      border-radius: 50%;
      pointer-events: none;
      z-index: 9999;
      display: none;
      overflow: hidden;
      box-shadow:
        0 0 0 2px rgba(201, 168, 76, 0.8),
        0 0 0 4px rgba(255,255,255,0.4),
        0 8px 32px rgba(0,0,0,0.25);
      background: #fff;
      transform: translate(-50%, -50%);
      transition: opacity 0.15s ease;
      will-change: transform;
    `;

    // Inner zoomed image
    const inner = document.createElement('div');
    inner.id = 'product-lens-inner';
    inner.style.cssText = `
      position: absolute;
      inset: 0;
      background-repeat: no-repeat;
      background-size: auto;
      pointer-events: none;
    `;

    // Crosshair
    const crosshair = document.createElement('div');
    crosshair.style.cssText = `
      position: absolute;
      inset: 0;
      pointer-events: none;
      z-index: 1;
    `;
    crosshair.innerHTML = `
      <svg width="100%" height="100%" viewBox="0 0 224 224" fill="none" xmlns="http://www.w3.org/2000/svg" style="position:absolute;inset:0;opacity:0.25;">
        <line x1="112" y1="84" x2="112" y2="140" stroke="rgba(201,168,76,0.8)" stroke-width="1"/>
        <line x1="84" y1="112" x2="140" y2="112" stroke="rgba(201,168,76,0.8)" stroke-width="1"/>
        <circle cx="112" cy="112" r="5" stroke="rgba(201,168,76,0.8)" stroke-width="1" fill="none"/>
      </svg>
    `;

    lens.appendChild(inner);
    lens.appendChild(crosshair);
    document.body.appendChild(lens);
    this.#lens = lens;
  }

  #attachToGalleries() {
    // Target the product information media area only (not collection cards)
    const galleries = document.querySelectorAll(
      '.product-information__media media-gallery, .product-information__media .product-media-gallery'
    );

    galleries.forEach((gallery) => {
      if (gallery.dataset.lensAttached) return;
      gallery.dataset.lensAttached = 'true';

      gallery.addEventListener('pointerenter', (e) => this.#onEnterGallery(e, gallery), { passive: true });
      gallery.addEventListener('pointerleave', () => this.#onLeaveGallery(), { passive: true });
    });
  }

  /**
   * @param {Event} e
   * @param {Element} gallery
   */
  #onEnterGallery(e, gallery) {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    gallery.addEventListener('pointermove', (ev) => this.#onMove(ev, gallery), { signal, passive: true });
    gallery.addEventListener('pointerleave', () => this.#hideLens(), { signal, passive: true });
  }

  #onLeaveGallery() {
    this.#hideLens();
    this.#controller?.abort();
  }

  /**
   * @param {PointerEvent} e
   * @param {Element} gallery
   */
  #onMove(e, gallery) {
    // Find the currently visible image
    const img = /** @type {HTMLImageElement | null} */ (
      gallery.querySelector('slideshow-slide[aria-hidden="false"] img') ||
      gallery.querySelector('.product-media-container img') ||
      gallery.querySelector('img')
    );

    if (!img || !img.complete || img.naturalWidth === 0) return;

    const rect = img.getBoundingClientRect();

    // Check cursor is over the image
    if (
      e.clientX < rect.left ||
      e.clientX > rect.right ||
      e.clientY < rect.top ||
      e.clientY > rect.bottom
    ) {
      this.#hideLens();
      return;
    }

    this.#showLens(e.clientX, e.clientY, img, rect);
  }

  /**
   * @param {number} cx - cursor X (viewport)
   * @param {number} cy - cursor Y (viewport)
   * @param {HTMLImageElement} img
   * @param {DOMRect} rect
   */
  #showLens(cx, cy, img, rect) {
    const lens = this.#lens;
    if (!lens) return;

    const inner = /** @type {HTMLDivElement} */ (document.getElementById('product-lens-inner'));

    // Position lens at cursor
    lens.style.left = `${cx}px`;
    lens.style.top = `${cy}px`;
    lens.style.display = 'block';

    // Calculate cursor position as fraction of image
    const fracX = (cx - rect.left) / rect.width;
    const fracY = (cy - rect.top) / rect.height;

    // Zoomed background size
    const bgW = rect.width * ZOOM_FACTOR;
    const bgH = rect.height * ZOOM_FACTOR;

    // Background position: center the zoomed point in the lens
    const bgX = fracX * bgW - LENS_SIZE / 2;
    const bgY = fracY * bgH - LENS_SIZE / 2;

    // Use the highest resolution src available
    const src = img.currentSrc || img.src;

    inner.style.backgroundImage = `url('${src}')`;
    inner.style.backgroundSize = `${bgW}px ${bgH}px`;
    inner.style.backgroundPosition = `-${bgX}px -${bgY}px`;
  }

  #hideLens() {
    if (this.#lens) {
      this.#lens.style.display = 'none';
    }
  }
}

// Only run on product pages
if (document.querySelector('.product-information__media')) {
  new ProductLensZoom();
}

// Also init after page navigation (if theme uses AJAX)
document.addEventListener('page:load', () => {
  if (document.querySelector('.product-information__media')) {
    new ProductLensZoom();
  }
});
